// A specialist's History: the Timeline section of its file, newest first, so the page
// says how the project got to where the Status card says it stands.
import { useMemo, useState } from "react";

import { formatDay } from "../utils/attributeFormat";
import { Markdown } from "./Markdown";
import { SectionBlock, SectionCard } from "./SpecialistRowSections";
import type { Citations } from "./useCitations";
import styles from "./HistoryCard.module.css";

const SHOWN_COLLAPSED = 5;

// "2026-10-01 - UKPD sends..." or "2026-10-13 (later) - ..." as its date and what
// happened; other shapes stay whole.
const ISO_LEAD_RE =
  /^(\d{4}-\d{2}-\d{2})(?:\s*\([^)]*\))?\s*[-\u2013\u2014:]\s*(.*)$/;

interface Event {
  iso: string | null;
  text: string;
}

const split = (entry: string): Event => {
  const match = ISO_LEAD_RE.exec(entry);
  return match ? { iso: match[1], text: match[2] } : { iso: null, text: entry };
};

// Newest first by date, not by reversing the order written: an agent adds an event it
// learned of late after newer ones. A day's later-written events come first, and an
// undated line keeps its place after the dated event written before it.
const byDate = (entries: string[]): Event[] => {
  let current = "";
  return entries
    .map((entry, order) => {
      const event = split(entry);
      if (event.iso) current = event.iso;
      return { event, sortKey: event.iso ?? current, order };
    })
    .sort((a, b) =>
      a.sortKey === b.sortKey
        ? b.order - a.order
        : a.sortKey < b.sortKey
          ? 1
          : -1,
    )
    .map(({ event }) => event);
};

export const HistoryCard = ({
  title,
  entries,
  citations,
}: {
  /** "History"; Building readiness's reads as what falls outside its three areas. */
  title: string;
  entries: string[];
  /** The tab's one lookup over every body it shows. */
  citations: Citations;
}) => {
  const [expanded, setExpanded] = useState(false);
  const newestFirst = useMemo(() => byDate(entries), [entries]);
  const shown = expanded ? newestFirst : newestFirst.slice(0, SHOWN_COLLAPSED);

  return (
    <SectionCard
      title={title}
      aside={
        <span className={styles.meta}>
          {newestFirst.length} event{newestFirst.length === 1 ? "" : "s"}
        </span>
      }
      id="history"
    >
      <SectionBlock>
        {newestFirst.length === 0 ? (
          <p className={styles.muted}>
            Nothing recorded yet. The agent logs each event on the project as it
            learns of it.
          </p>
        ) : (
          <>
            <ol className={styles.events}>
              {shown.map(({ iso, text }, i) => {
                return (
                  <li
                    key={`${i}-${text.slice(0, 40)}`}
                    className={styles.event}
                  >
                    {iso && (
                      <span className={styles.day}>{formatDay(iso)}</span>
                    )}
                    <span className={styles.text}>
                      <Markdown components={citations.components} inline>
                        {citations.transform(text)}
                      </Markdown>
                    </span>
                  </li>
                );
              })}
            </ol>
            {newestFirst.length > SHOWN_COLLAPSED && (
              <button
                type="button"
                className={styles.toggle}
                onClick={() => setExpanded((open) => !open)}
              >
                {expanded
                  ? "Show the latest only"
                  : `Show all ${newestFirst.length} events`}
              </button>
            )}
          </>
        )}
      </SectionBlock>
    </SectionCard>
  );
};
